import { FC, useMemo, useEffect, useState } from 'react';
import { Form } from 'formik';
import { useQuery } from 'react-query';
import Launch from '@mui/icons-material/Launch';
// Components
import {
  Modal,
  Loader,
  TextField,
  SelectAsync,
  Wysiwyg,
  ModalSaveSection
} from '../../../../components';
import {
  Box,
  Fade,
  Grid,
  TextField as MuiTextField,
  FormControl,
  Autocomplete,
  FormHelperText,
  InputAdornment,
  Typography
} from '@mui/material';
import 'react-quill/dist/quill.snow.css';
// fetch
import {
  getNewsCategories,
  getCustomPages,
  getNewsItemTypes,
  getVendors,
  getDomainUsers,
  getNewsVisibilities,
  getClientsSimple,
  getSubcontractorsTypes,
  getAgentsSimple
} from '../../../../fetch';
import {
  IDropdownResponse,
  IResponse,
  IDomainUser,
  IVendor,
  ICustomPage,
  INewsDetail,
  INewsVisibilities,
  IPeopleSimple,
  ISubcontractorTypeRes,
  ISubcontractorType
} from '../../../../models';
import { NewsFile } from './news-files';
import { DatePicker } from '@mui/x-date-pickers';

interface INewsItemModalContent {
  open: boolean;
  handleClose: () => void;
  currentNewsItem?: INewsDetail | null;
  setFieldValue: (key: string, val: any) => void;
  values: any;
  errors: any;
  handleSubmit: () => void;
  isSubmitting: boolean;
  dirty: boolean;
  isValid: boolean;
  isLoadingModalContent?: boolean;
  file: any;
  setFile: React.Dispatch<React.SetStateAction<any>>;
  resetForm: (nextState: any) => void;
}

export const NewsItemModalContent: FC<INewsItemModalContent> = ({
  open,
  handleClose,
  currentNewsItem,
  setFieldValue,
  values,
  errors,
  handleSubmit,
  isSubmitting,
  dirty,
  isValid,
  isLoadingModalContent,
  file,
  setFile,
  resetForm
}) => {
  const [visibilityValues, setVisibilityValues] = useState<INewsVisibilities[]>([]);
  const [inputVisibilityValue, setInputVisibilityValue] = useState('');
  const [employeeValues, setEmployeeValues] = useState<IDomainUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [vendorValues, setVendorValues] = useState<IVendor[]>([]);
  const [inputVendorValue, setInputVendorValue] = useState('');

  const [homeownerValues, setHomeownerValues] = useState<IPeopleSimple[]>([]);
  const [inputHomeownerValue, setInputHomeownerValue] = useState('');

  const [salesAgentValues, setSalesAgentValues] = useState<IPeopleSimple[]>([]);
  const [inputSalesAgentValue, setInputSalesAgentValue] = useState('');

  const { isLoading: isLoadingInternalUsers, data: internalUsersData } = useQuery<
    IResponse<IDomainUser[]>,
    Error
  >(['getDomainUsers-modal'], () =>
    getDomainUsers({
      sortBy: 'firstName'
    })
  );
  const internalUsers: IDomainUser[] = useMemo(
    () => internalUsersData?.records ?? [],
    [internalUsersData]
  );

  const { isLoading: isLoadingVendors, data: vendorsData } = useQuery<IResponse<IVendor[]>, Error>(
    ['getVendors-modal'],
    () =>
      getVendors({
        perPage: '2147483647'
      })
  );
  const addtionalVendors: IVendor[] = useMemo(() => vendorsData?.records ?? [], [vendorsData]);

  const [subcontractorTypesValues, setSubcontractorTypesValues] = useState<ISubcontractorType[]>(
    []
  );
  const [inputSubcontractorTypesValue, setInputSubcontractorTypesValue] = useState('');
  const { isLoading: isLoadingSubcontractorTypes, data: subcontractorTypesData } = useQuery<
    ISubcontractorTypeRes,
    Error
  >(['getSubcontractors-modal'], () => getSubcontractorsTypes());

  const subcontractorTypes: ISubcontractorType[] = useMemo(
    () => subcontractorTypesData?.records ?? [],
    [subcontractorTypesData]
  );

  const { isLoading: isLoadingClients, data: clientsData } = useQuery<
    IResponse<IPeopleSimple[]>,
    Error
  >(['getClients-modal'], () => getClientsSimple());
  const additionalClients: IPeopleSimple[] = useMemo(
    () => clientsData?.records ?? [],
    [clientsData]
  );

  const { isLoading: isLoadingAgents, data: agentsData } = useQuery<
    IResponse<IPeopleSimple[]>,
    Error
  >(['getAgents-modal'], () => getAgentsSimple());
  const additionalAgents: IPeopleSimple[] = useMemo(() => agentsData?.records ?? [], [agentsData]);

  const { isLoading: isLoadingVisibilities, data: visibilitiesData } = useQuery<
    INewsVisibilities[],
    Error
  >(['getVisibilities-modal'], () => getNewsVisibilities());
  const visibilityOptions: INewsVisibilities[] = useMemo(
    () => visibilitiesData ?? [],
    [visibilitiesData]
  );

  useEffect(() => {
    if (currentNewsItem?.vendors?.length > 0 && addtionalVendors?.length) {
      const currentVendorIds = currentNewsItem?.vendors.map(v => v.vendorId);
      const selected = addtionalVendors.filter(v => currentVendorIds.includes(v.vendorId));
      setVendorValues(selected);
    }
  }, [addtionalVendors, currentNewsItem]);

  useEffect(() => {
    if (currentNewsItem?.usernames?.length > 0 && internalUsers?.length) {
      const selected = internalUsers.filter(user =>
        currentNewsItem?.usernames
          .map(u => u.toLowerCase())
          .includes(user.accountName.toLowerCase())
      );
      setEmployeeValues(selected);
    }
  }, [internalUsers, currentNewsItem]);

  useEffect(() => {
    if (currentNewsItem?.homeownerIds?.length > 0 && additionalClients?.length) {
      const currentHomeownerIds = currentNewsItem?.homeownerIds;
      const selected = additionalClients.filter(c => currentHomeownerIds.includes(c.personId));

      setHomeownerValues(selected);
    }
  }, [additionalClients, currentNewsItem]);

  useEffect(() => {
    if (currentNewsItem?.salesAgentIds?.length > 0 && additionalAgents?.length) {
      const currentSalesAgentIds = currentNewsItem?.salesAgentIds;
      const selected = additionalAgents.filter(c => currentSalesAgentIds.includes(c.personId));

      setSalesAgentValues(selected);
    }
  }, [additionalAgents, currentNewsItem]);

  useEffect(() => {
    if (currentNewsItem?.subcontractorTypeIds?.length > 0 && subcontractorTypes?.length) {
      const subcontractorTypesIds = currentNewsItem?.subcontractorTypeIds;
      const selected = subcontractorTypes.filter(c =>
        subcontractorTypesIds.includes(c.subcontractorTypeId)
      );

      setSubcontractorTypesValues(selected);
    }
  }, [subcontractorTypes, currentNewsItem]);

  useEffect(() => {
    if (currentNewsItem?.visibilities?.length > 0 && visibilityOptions?.length) {
      const currentVisibilities = currentNewsItem?.visibilities.map(v => v.value);
      const selected = visibilityOptions.filter(v => currentVisibilities.includes(v.value));
      setVisibilityValues(selected);
    }
  }, [visibilityOptions, currentNewsItem]);

  useEffect(() => {
    // if Specific Users are not in the list, reset vendors, homeowners, vendor sub types, and employees arrays.
    if (!values.visibilities.includes('SpecificUsers')) {
      setFieldValue('usernames', []);
      setFieldValue('vendorIds', []);
      setFieldValue('homeownerIds', []);
      setFieldValue('salesAgentIds', []);
      setFieldValue('subcontractorTypeIds', []);
      setVendorValues([]);
      setEmployeeValues([]);
      setHomeownerValues([]);
      setSalesAgentValues([]);
      setSubcontractorTypesValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.visibilities]);

  const getTargetingHelperText = () => {
    if (visibilityValues.filter(v => v.value === 'AllUsers').length > 0) {
      return ' Please note, this news item will appear for all Employees, Vendors, Homeowners, and Sales Agents.';
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
        setVisibilityValues([]);
      }}
      maxWidth="md"
      title={currentNewsItem ? 'Edit News Item' : 'Add News Item'}
    >
      {(isSubmitting || isLoadingModalContent) && <Loader type="overlay" position="centered" />}
      <Fade in={open}>
        <Form onSubmit={handleSubmit} autoComplete="none">
          <Box mt={1}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <SelectAsync
                  label="Category"
                  name="category"
                  required
                  onClear={() => {
                    setFieldValue('category', '');
                  }}
                  hasClear
                  apiRequest={() => getNewsCategories()}
                  transformResponse={(res: IDropdownResponse[]) =>
                    res.map(r => ({
                      label: r.description,
                      value: r.value
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="subject" label="Subject" required />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Targeting</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="newsTarget"
                    multiple
                    options={visibilityOptions}
                    value={visibilityValues}
                    onChange={(event, newValue: INewsVisibilities[]) => {
                      const newValues = newValue?.map(v => v.value);
                      const latestOption = newValue?.[newValue.length - 1]?.value;
                      const isLatestOthers =
                        latestOption === 'AllEmployees' ||
                        latestOption === 'AllVendors' ||
                        latestOption === 'SpecificUsers' ||
                        latestOption === 'AllHomeowners' ||
                        latestOption === 'AllSales'
                          ? true
                          : false;
                      const isLatestAllUsers = latestOption === 'AllUsers' ? true : false;
                      const isLatestEmployees = latestOption === 'AllEmployees' ? true : false;
                      const isLatestVendors = latestOption === 'AllVendors' ? true : false;
                      const isLatestAllHomeowners = latestOption === 'AllHomeowners' ? true : false;
                      const isLatestSalesAgents = latestOption === 'AllSales' ? true : false;
                      const isLatestSpecific = latestOption === 'SpecificUsers' ? true : false;

                      // only show "All users"
                      if (isLatestAllUsers) {
                        const correctOptions = newValue.filter(v => v.value === 'AllUsers');
                        setVisibilityValues(correctOptions);
                        setFieldValue(
                          'visibilities',
                          correctOptions.map(val => val.value)
                        );
                      } else if (isLatestAllHomeowners) {
                        const correctOptions = newValue.filter(v => v.value === 'AllHomeowners');
                        setVisibilityValues(correctOptions);
                        setFieldValue(
                          'visibilities',
                          correctOptions.map(val => val.value)
                        );
                      } else if (isLatestSalesAgents) {
                        const correctOptions = newValue.filter(v => v.value === 'AllSales');
                        setVisibilityValues(correctOptions);
                        setFieldValue(
                          'visibilities',
                          correctOptions.map(val => val.value)
                        );
                      } else if (isLatestOthers) {
                        // only show Specific Users and Employees
                        if (isLatestEmployees && newValues.includes('SpecificUsers')) {
                          const correctOptions = newValue.filter(v => v.value !== 'AllVendors');
                          setVisibilityValues(correctOptions);
                          setFieldValue(
                            'visibilities',
                            correctOptions.map(val => val.value)
                          );
                          // only show Specific Users and Vendors
                        } else if (isLatestVendors && newValues.includes('SpecificUsers')) {
                          const correctOptions = newValue.filter(v => v.value !== 'AllEmployees');
                          setVisibilityValues(correctOptions);
                          setFieldValue(
                            'visibilities',
                            correctOptions.map(val => val.value)
                          );
                        } else if (
                          isLatestSpecific &&
                          (newValues.includes('AllVendors') || newValues.includes('AllEmployees'))
                        ) {
                          // only show "Specifc users"
                          const correctOptions = newValue.filter(
                            v =>
                              v.value !== 'AllUsers' &&
                              v.value !== 'AllEmployees' &&
                              v.value !== 'AllVendors'
                          );
                          setVisibilityValues(correctOptions);
                          setFieldValue(
                            'visibilities',
                            correctOptions.map(val => val.value)
                          );
                        } else {
                          // clear out "All Users" when you select any other option
                          const correctOptions = newValue.filter(v => v.value !== 'AllUsers');
                          setVisibilityValues(correctOptions);
                          setFieldValue(
                            'visibilities',
                            correctOptions.map(val => val.value)
                          );
                        }
                        // cleared the options
                      } else {
                        setVisibilityValues([]);
                        setFieldValue('visibilities', []);
                      }
                    }}
                    inputValue={inputVisibilityValue}
                    onInputChange={(event, newInputValue) => {
                      setInputVisibilityValue(newInputValue);
                    }}
                    getOptionLabel={option => option.shorthand}
                    filterSelectedOptions
                    renderOption={(props, option: INewsVisibilities) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <MuiTextField
                        {...params}
                        disabled={isLoadingVisibilities}
                        label={isLoadingVisibilities ? 'Loading...' : 'News Item Target'}
                        placeholder="Select Target"
                        required
                        helperText={getTargetingHelperText()}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {visibilityValues.map(v => v.value).includes('SpecificUsers') && (
                <>
                  {!visibilityValues.map(v => v.value).includes('AllEmployees') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          multiple
                          id="employees"
                          value={employeeValues}
                          onChange={(event, newValue: any[]) => {
                            setEmployeeValues(newValue);
                            setFieldValue(
                              'usernames',
                              newValue.map(val => val.accountName)
                            );
                          }}
                          inputValue={inputValue}
                          onInputChange={(event, newInputValue) => {
                            setInputValue(newInputValue);
                          }}
                          options={internalUsers}
                          getOptionLabel={option => option.displayName}
                          filterSelectedOptions
                          renderOption={(props, option: IDomainUser) => {
                            return (
                              <li {...props} key={`${option.accountName}`}>
                                {option.displayName}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              disabled={isLoadingInternalUsers}
                              label={isLoadingInternalUsers ? 'Loading...' : 'Employees'}
                              placeholder="Add Employee"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  {!visibilityValues.map(v => v.value).includes('AllHomeowners') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          multiple
                          id="homeowners"
                          value={homeownerValues}
                          onChange={(event, newValue: any[]) => {
                            setHomeownerValues(newValue);
                            setFieldValue(
                              'homeownerIds',
                              newValue.map(val => val.personId)
                            );
                          }}
                          inputValue={inputHomeownerValue}
                          onInputChange={(event, newInputValue) => {
                            setInputHomeownerValue(newInputValue);
                          }}
                          options={additionalClients}
                          getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                          filterSelectedOptions
                          renderOption={(props, option: IPeopleSimple) => {
                            return (
                              <li {...props} key={`${option.personId}`}>
                                {option.firstName} {option.lastName}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              disabled={isLoadingClients}
                              label={isLoadingClients ? 'Loading...' : 'Homeowners'}
                              placeholder="Add Homeowners"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  {!visibilityValues.map(v => v.value).includes('AllSales') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          multiple
                          id="salesAgents"
                          value={salesAgentValues}
                          onChange={(_, newValue: any[]) => {
                            setSalesAgentValues(newValue);
                            setFieldValue(
                              'salesAgentIds',
                              newValue.map(val => val.personId)
                            );
                          }}
                          inputValue={inputSalesAgentValue}
                          onInputChange={(_, newInputValue) => {
                            setInputSalesAgentValue(newInputValue);
                          }}
                          options={additionalAgents}
                          getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                          filterSelectedOptions
                          renderOption={(props, option: IPeopleSimple) => {
                            return (
                              <li {...props} key={`${option.personId}`}>
                                {option.firstName} {option.lastName}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              disabled={isLoadingAgents}
                              label={isLoadingAgents ? 'Loading...' : 'Sales Agents'}
                              placeholder="Add Sales Agents"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  {!visibilityValues.map(v => v.value).includes('AllVendors') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          multiple
                          id="vendors"
                          value={vendorValues}
                          onChange={(event, newValue: any[]) => {
                            setVendorValues(newValue);
                            setFieldValue(
                              'vendorIds',
                              newValue.map(val => val.vendorId)
                            );
                          }}
                          inputValue={inputVendorValue}
                          onInputChange={(event, newInputValue) => {
                            setInputVendorValue(newInputValue);
                          }}
                          options={addtionalVendors}
                          getOptionLabel={option => option.name}
                          filterSelectedOptions
                          renderOption={(props, option: IVendor) => {
                            return (
                              <li {...props} key={`${option.vendorId}`}>
                                {option.name}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              disabled={isLoadingVendors}
                              label={isLoadingVendors ? 'Loading...' : 'Vendors'}
                              placeholder="Add Vendor"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  {!visibilityValues.map(v => v.value).includes('AllVendors') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Autocomplete
                          multiple
                          id="subcontractors"
                          value={subcontractorTypesValues}
                          onChange={(event, newValue: any[]) => {
                            setSubcontractorTypesValues(newValue);
                            setFieldValue(
                              'subcontractorTypeIds',
                              newValue.map(val => val.subcontractorTypeId)
                            );
                          }}
                          inputValue={inputSubcontractorTypesValue}
                          onInputChange={(event, newInputValue) => {
                            setInputSubcontractorTypesValue(newInputValue);
                          }}
                          options={subcontractorTypes}
                          getOptionLabel={option => `${option.name}`}
                          filterSelectedOptions
                          renderOption={(props, option: ISubcontractorType) => {
                            return (
                              <li {...props} key={`${option.subcontractorTypeId}`}>
                                {option.name}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              disabled={isLoadingSubcontractorTypes}
                              label={
                                isLoadingSubcontractorTypes
                                  ? 'Loading...'
                                  : 'Vendors by Subcontractor'
                              }
                              placeholder="Vendors by Subcontractor"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="h6">News Content</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectAsync
                  label="Content Type"
                  required
                  name="newsItemType"
                  onClear={() => {
                    setFieldValue('newsItemType', '');
                  }}
                  hasClear
                  apiRequest={() => getNewsItemTypes()}
                  transformResponse={(res: IDropdownResponse[]) =>
                    res.map(r => ({
                      label: r.description,
                      value: r.value
                    }))
                  }
                />
              </Grid>
              {values?.newsItemType === 'CustomPage' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <SelectAsync
                      label="Custom Page"
                      name="customPageId"
                      onClear={() => {
                        setFieldValue('customPageId', '');
                      }}
                      hasClear
                      apiRequest={() =>
                        getCustomPages({
                          perPage: '2147483647'
                        })
                      }
                      transformResponse={(res: IResponse<ICustomPage[]>) =>
                        res.records.map(r => ({
                          label: r.name,
                          value: `${r.customPageId}`
                        }))
                      }
                    />
                    <FormHelperText>Select a custom page to link this news item to.</FormHelperText>
                  </FormControl>
                </Grid>
              )}
              {values?.newsItemType === 'ExternalUrl' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <TextField
                      required
                      name="externalUrl"
                      label="External URL"
                      // @ts-ignore
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Launch
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                const url = values.externalUrl;
                                if (url) {
                                  window.open(url, '_blank');
                                }
                              }}
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                    <FormHelperText>Example of a valid URL: https://example.com</FormHelperText>
                  </FormControl>
                </Grid>
              )}
              {values?.newsItemType === 'AttachedFile' && (
                <Grid item xs={12} sm={6}>
                  <NewsFile
                    newsItemId={currentNewsItem?.newsItemId}
                    imageTitle={'News File:'}
                    imageData={value => setFieldValue('fileData', value)}
                    image={values?.fileData}
                    currentNewsItem={currentNewsItem}
                    file={file}
                    setFile={setFile}
                    values={values}
                    resetForm={resetForm}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Wysiwyg
                  id="news-message"
                  label="Message"
                  isRequired
                  value={values.message}
                  onChange={val => setFieldValue('message', val)}
                  placeholder="Add Message..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box mt={1}>
                  <DatePicker
                    label="Display Date"
                    value={values.displayDate}
                    onChange={(date: any) => {
                      setFieldValue('displayDate', date);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!errors?.targetDate,
                        required: true,
                        fullWidth: true
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box mt={1}>
                  <DatePicker
                    label="Takedown Date"
                    value={values.takeDownDate}
                    minDate={values.displayDate}
                    onChange={(date: any) => {
                      setFieldValue('takeDownDate', date);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        required: true,
                        fullWidth: true
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <ModalSaveSection
              handleSave={() => {
                handleSubmit();
                setVisibilityValues([]);
              }}
              handleCancel={() => {
                handleClose();
                setVisibilityValues([]);
              }}
              isSaveDisabled={!dirty || isSubmitting || !isValid}
            />
          </Box>
        </Form>
      </Fade>
    </Modal>
  );
};
